import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route } from "react-router-dom";
import * as R from "ramda";
import { connect, useSelector, useDispatch } from "react-redux";
import { Icon, Switch, Tooltip } from "antd";

import * as gtag from "../../../../lib/gtag";
import { ConditionalWrapper } from "../../../../components/ConditionalWrapper";
import Pagination from "../../../../components/transforms/Pagination";
import NonResults from "../../../../components/transforms/filters/NonResults";
import ActionsSnackbar from "../../../../components/ActionsSnackbar/ActionsSnackbar";
import {
  CANDIDATE_LISTS,
  FILTERS_FOR,
  EDUCATION_LEVELS
} from "../../../../constants/opportunity";
import { getTags as getChatbotTags } from "../../../../components/Chatbot/selectors";
import { INITIAL_CHATBOT_VALIDATION_PATH } from "../../../../components/Chatbot/validators";
import {
  FETCH_PROFESSIONS,
  FETCH_SKILLS,
  FETCH_STUDIES
} from "../../../../store/actions/catalogs";
import useListBatchActions from "../OpportunityCandidates/useListBatchActions";
import CustomPagination from "../../../../components/transforms/CustomPagination";
import { CANDIDATES_PER_PAGE } from "../../../../utils/candidatesFilters";
import { useCandidatesData } from "../../../../hooks/custom/useCandidatesData";
import { usePaginationLogic } from "../../../../hooks/custom/usePaginationLogic";
import { isRecent } from "../../../../utils/dates";
import { markCandidatesAsRead } from "../../../../store/actions/opportunity";
import { getLanguages } from "../../../../utils/data/static/languages";
import { ACCEPT_LANGUAGE } from "../../../../locales/acceptedLanguages";
import { tagNames } from "../../../../components/CandidateModal/Tests/SuccessProbabilityTag";

import { SkeletonCandidates } from "./Skeleton";
import CandidateDetails from "./CandidateDetails";
import CandidateListCard from "./CandidateListCard";
import StrictFilters from "./filters/StrictFilters";
import SearchFilter from "./filters/SearchFilter";
import ReferralFilter from "./filters/ReferralFilter";
import ChatbotTagsFilters from "./filters/ChatbotTagsFilters";
import useFilters, {
  DEFAULT_SERVER_FILTERS_FOR_OPPORTUNITY
} from "./filters/useFilters";
import ShareCandidatesModal from "./ShareCandidatesModal";
import SourceFilter from "./filters/SourceFilter";
import ExperienceFilters from "./filters/ExperienceFilters";
import SalaryFilter from "./filters/SalaryFilter";
import CheckboxFilters from "./filters/CheckboxFilter";
import AddressFilter from "./filters/AddressFilter";
import AgeFilters from "./filters/AgeFilters";
import {
  Header,
  Content,
  Sidebar,
  SearchContainer,
  Grid,
  Container,
  Title,
  StyledRadioGroup,
  StyledCheckbox
} from "./filters/FilterComponents";
import SortBy from "./filters/SortBy";
import RangeFilters from "./filters/RangeFilters";
import CandidatesTagsModal from "./CandidatesTagsModal";
import CandidateTagsFilters from "./filters/CandidateTagsFilters";
import DateFilter from "./filters/DateFilter";

import styled from "styled-components/macro";

const emptyListMessages = {
  [CANDIDATE_LISTS.hunting]: "emptyHuntingList",
  [CANDIDATE_LISTS.suggested]: "emptySuggestedList"
};

const sortOptions = R.sortBy(
  R.compose(
    R.toLower,
    R.prop("label")
  )
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WrapperFilters = styled.div`
  background: ${({ theme }) => theme["@gray-20"]};
  height: 100px;
  padding: 20px;
  max-width: 280px;
  margin-bottom: 20px;
`;

const TextFilters = styled.div`
  font-size: 10px;
  color: "#747D84";
  margin-left: 5px;
`;

const WrapperSwitch = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const Cards = styled.div`
  width: 100%;
`;

const CandidatesSection = ({
  candidatesData,
  history,
  routeMatch,
  updateCandidateStatus,
  opportunity,
  loading,
  list,
  selectedCandidatesForOpportunity = [],
  selectedCandidatesForOpportunityUnavailable = false,
  canMove,
  canChat,
  restrictAdvanceToProcessByScreening,
  onServerFiltersChange,
  multipleSelectCandidate,
  multipleSelectedCandidates,
  resetMultipleSelectCandidates,
  listBatchAction,
  listBatchInvite,
  candidatesBatchShare,
  markCandidatesAsRead,
  serverFiltersList = DEFAULT_SERVER_FILTERS_FOR_OPPORTUNITY
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    gtag.event({
      category: "VacancyCandidates",
      action: list,
      label: opportunity.id
    });
  }, [list, opportunity.id]);

  const { t, i18n } = useTranslation();
  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;
  const [
    shareCandidatesModalVisible,
    setShareCandidatesModalVisible
  ] = useState(false);

  const [candidatesTagsModalVisible, setCandidatesTagsModalVisible] = useState(
    false
  );

  const fieldsOfStudyList = useSelector(
    state => FETCH_STUDIES.getResponse(state) || []
  );

  const professionList = useSelector(
    state => FETCH_PROFESSIONS.getResponse(state) || []
  );

  const skillList = useSelector(state => FETCH_SKILLS.getResponse(state) || []);

  const { candidates, metadata: candidatesMetadata = false } = useMemo(
    () => candidatesData,
    [candidatesData]
  );

  const hasSuccessProbabilityTest = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "successProbabilityTest"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "successProbabilityTest"],
        state
      );
    }
    return res;
  });

  const successProbabilityOptions = R.map(
    value => ({
      label: t(
        `opportunity:process.candidateDetails.successProbability-worcket.${value}`
      ),
      value
    }),
    R.values(tagNames)
  );

  const screeningStatusOptions = R.map(
    value => ({
      value,
      label: t(
        `opportunity:candidates.screeningStatusFilters.${value || "all"}`
      )
    }),
    ["", "completed", "pending", "passed", "disapproved"]
  );

  const languageOptions = R.pipe(
    getLanguages,
    R.toPairs,
    R.map(([value, label]) => ({ value, label })),
    sortOptions
  )();

  const educationOptions = EDUCATION_LEVELS.map(level => ({
    value: level,
    label: t(`catalogs:educationLevelsAdvanced.${level}`)
  }));

  const fieldsOfStudyOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.field,
          value: item.field
        })),
        sortOptions
      )(fieldsOfStudyList),
    [fieldsOfStudyList, locale]
  );

  const skillOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.skill,
          value: item.skill
        })),
        sortOptions
      )(skillList),
    [skillList, locale]
  );

  const professionOptions = useMemo(
    () =>
      R.pipe(
        R.map(item => ({
          label: item[locale] || item.label,
          value: item.id
        })),
        sortOptions
      )(professionList),
    [locale, professionList]
  );

  const activeFilters = useMemo(() => FILTERS_FOR[list], [list]);
  const hasFilters = useMemo(
    () =>
      R.compose(
        R.not,
        R.isEmpty,
        R.reject(R.equals("registered")),
        R.keys
      )(activeFilters),
    [activeFilters]
  );

  const {
    filteredCandidates,
    filters,
    handleFiltersChange,
    strictFilterOptions,
    isEmpty,
    areFiltersApplied
  } = useFilters({
    list,
    filterKey: `${opportunity.id}_${list}`,
    filtersUsed: activeFilters,
    candidates,
    opportunity,
    onServerFiltersChange,
    resetMultipleSelectCandidates,
    serverFiltersList,
    opportunityFilters: false
  });

  let sortedFilteredCandidates = filteredCandidates;

  if (list === CANDIDATE_LISTS.suggested) {
    // Add isRecent property to each candidate
    sortedFilteredCandidates = filteredCandidates.map(c => {
      const applyTime =
        c.opportunityProcessStatus &&
        c.opportunityProcessStatus.length &&
        c.opportunityProcessStatus[0].applyTime;
      c.isRecent = isRecent(applyTime);

      return c;
    });

    // // Recent candidates should appear first in the list
    // const [recentCandidates, otherCandidates] = R.partition(
    //   R.propEq("isRecent", true),
    //   sortedFilteredCandidates
    // );
    // sortedFilteredCandidates = [...recentCandidates, ...otherCandidates];
  }

  const { metadata, mappedMetadata } = useCandidatesData({
    candidates,
    metadata: candidatesMetadata,
    filters
  });

  const {
    handleListBatchShare,
    handleSelectAllChange,
    plurals,
    selectAllStatus,
    snackbarActions
  } = useListBatchActions({
    opportunity,
    candidates,
    dispatch,
    filteredCandidates: sortedFilteredCandidates,
    list,
    listBatchAction,
    listBatchInvite,
    candidatesBatchShare,
    markCandidatesAsRead,
    multipleSelectedCandidates,
    setShareCandidatesModalVisible,
    setCandidatesTagsModalVisible,
    canMove,
    restrictAdvanceToProcessByScreening,
    t
  });

  const chatbotQuestions = useMemo(
    () =>
      R.pipe(
        R.propOr([], "questionsAdvanced"),
        R.find(R.propEq("type", "chatbot")),
        R.pathOr([], INITIAL_CHATBOT_VALIDATION_PATH)
      )(opportunity),
    [opportunity]
  );

  const chatbotTags = useMemo(
    () => chatbotQuestions && getChatbotTags(chatbotQuestions).sort(),
    [chatbotQuestions]
  );

  const tags = useSelector(state =>
    R.pathOr([], ["company", "tags"], state).sort()
  );

  usePaginationLogic({
    candidates,
    filters,
    filtersUsed: activeFilters,
    handleFiltersChange,
    loading,
    mappedMetadata
  });

  // sessionStorage.removeItem(`${opportunity.id}_${list}.v3.7`);

  const ageEquals = R.equals(
    R.propOr([16, 65], "agerange", filters),
    R.when(
      R.isEmpty(),
      R.always([16, 65]),
      R.propOr([], "ageRange", opportunity)
    )
  );

  const studyEquals = R.equals(
    R.propOr([], "fieldsofstudy", filters),
    R.propOr([], "fieldsOfStudy", opportunity)
  );

  const educationEquals = R.equals(
    R.propOr([], "education", filters),
    R.propOr([], "educationLevelsAdvanced", opportunity).map(e => e.level)
  );

  const languagesEquals =
    R.length(R.propOr([], "languages", opportunity)) === 0 ||
    R.propOr([], "strictFilters", filters).includes("languages");

  const isFiltersEquals =
    ageEquals && studyEquals && educationEquals && languagesEquals;

  const hasStrictFilter =
    R.length(R.propOr([], "ageRange", opportunity)) > 0 ||
    R.length(R.propOr([], "fieldsOfStudy", opportunity)) > 0 ||
    R.length(R.propOr([], "educationLevelsAdvanced", opportunity)) > 0 ||
    R.length(R.propOr([], "languages", opportunity)) > 0;

  const hasScreening = useMemo(
    () =>
      opportunity.englishTestRequired ||
      R.any(R.propEq("enabled", true), opportunity.questionsAdvanced || []),
    [opportunity.englishTestRequired, opportunity.questionsAdvanced]
  );

  return (
    <>
      <Header>
        {activeFilters.registered && (
          <SourceFilter
            loading={loading}
            metadata={metadata}
            onChange={handleFiltersChange("registered")}
            value={filters.registered}
          />
        )}
        <SearchContainer>
          <SearchFilter
            loading={loading}
            searchText={filters.searchText}
            setSearchText={handleFiltersChange("searchText")}
          />
        </SearchContainer>
      </Header>
      <ShareCandidatesModal
        onOk={handleListBatchShare}
        onClose={() => setShareCandidatesModalVisible(false)}
        visible={shareCandidatesModalVisible}
      />
      <CandidatesTagsModal
        onClose={() => setCandidatesTagsModalVisible(false)}
        visible={candidatesTagsModalVisible}
        candidates={multipleSelectedCandidates}
      />
      <Content>
        {hasFilters && (
          <Wrapper>
            {list === CANDIDATE_LISTS.suggested && hasStrictFilter && (
              <WrapperFilters>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <h4 style={{ color: "#3F3F3F", fontSize: 14, margin: 0 }}>
                    {t("opportunity:candidates.strictFilters.title")}
                  </h4>
                  <Tooltip
                    title={
                      <div style={{ padding: 5 }}>
                        {t("opportunity:candidates.strictFilters.tooltip")}
                      </div>
                    }
                  >
                    <Icon
                      type="info-circle"
                      style={{
                        marginLeft: 10,
                        color: "#9CA8B1",
                        fontSize: 16
                      }}
                    />
                  </Tooltip>
                </div>
                <WrapperSwitch>
                  <Switch
                    data-test="switch-strict-filters"
                    checked={isFiltersEquals}
                    onChange={value => {
                      if (value) {
                        handleFiltersChange("all")();
                      } else {
                        handleFiltersChange("none")();
                      }
                    }}
                  />
                  <TextFilters>
                    {t("opportunity:candidates.strictFilters.switchTitle")}
                  </TextFilters>
                </WrapperSwitch>
              </WrapperFilters>
            )}
            <Sidebar>
              {activeFilters.strictFilters && strictFilterOptions.length > 0 && (
                <StrictFilters
                  // link={`/oportunidades/${opportunity.id}/detalles/${t(
                  //   "opportunity:candidates.sectionName"
                  // )}`}
                  setStrictFilters={handleFiltersChange("strictFilters")}
                  value={filters.strictFilters}
                  options={strictFilterOptions}
                  loading={loading}
                />
              )}
              {activeFilters.tagprofile && hasSuccessProbabilityTest && (
                <CheckboxFilters
                  maxLength={3}
                  loading={loading}
                  title={t(
                    "opportunity:process.candidateDetails.successProbability-worcket.title"
                  )}
                  setValues={handleFiltersChange("tagprofile")}
                  values={filters.tagprofile || []}
                  options={successProbabilityOptions}
                />
              )}
              {hasScreening && activeFilters.screeningstatus && (
                <details open={!R.isEmpty(filters.screeningstatus)}>
                  <Title>
                    <h4>
                      {t("opportunity:candidates.screeningStatusFilters.title")}
                    </h4>
                  </Title>
                  <Container>
                    <StyledRadioGroup
                      loading={loading}
                      options={screeningStatusOptions}
                      onChange={handleFiltersChange("screeningstatus")}
                      value={filters.screeningstatus}
                    />
                  </Container>
                </details>
              )}
              {[CANDIDATE_LISTS.suggested, CANDIDATE_LISTS.hunting].includes(
                list
              ) && (
                <details open={!!filters.previousmatch}>
                  <Title>
                    <h4>
                      {t("opportunity:candidates.interactionFilters.title")}
                    </h4>
                  </Title>
                  <Container>
                    <StyledCheckbox
                      loading={loading}
                      checked={filters.previousmatch}
                      onChange={ev =>
                        handleFiltersChange("previousmatch")(ev.target.checked)
                      }
                    >
                      {t(
                        "opportunity:candidates.interactionFilters.previousMatch"
                      )}
                    </StyledCheckbox>
                  </Container>
                </details>
              )}
              {activeFilters.applydate && (
                <DateFilter
                  title={t("opportunity:candidates.applyDateFilters.title")}
                  loading={loading}
                  value={filters.applydate}
                  onChange={handleFiltersChange("applydate")}
                />
              )}
              {activeFilters.score && (
                <RangeFilters
                  title={t("opportunity:candidates.scoreFilters.title")}
                  filterName="score"
                  onChange={handleFiltersChange("score")}
                  loading={loading}
                  value={filters.score || []}
                  load={value => value.map(x => x * 100)}
                  store={value => value.map(x => x / 100)}
                  min={0}
                  max={100}
                  marks={{
                    0: "0%",
                    25: "25%",
                    50: "50%",
                    75: "75%",
                    100: "100%"
                  }}
                />
              )}
              {activeFilters.fieldsofstudy && (
                <CheckboxFilters
                  maxLength={4}
                  loading={loading}
                  title={t("opportunity:candidates.fieldsStudyFilters.title")}
                  setValues={handleFiltersChange("fieldsofstudy")}
                  values={filters.fieldsofstudy || []}
                  options={fieldsOfStudyOptions}
                  viewMoreLabels={t(
                    "opportunity:candidates.fieldsStudyFilters.viewMore"
                  )}
                  sorted={true}
                  grouped={true}
                  placeholder={t(
                    "opportunity:candidates.fieldsStudyFilters.placeholder"
                  )}
                />
              )}
              {activeFilters.professions && (
                <CheckboxFilters
                  maxLength={4}
                  loading={loading}
                  title={t("opportunity:candidates.professionFilters.title")}
                  setValues={handleFiltersChange("professions")}
                  values={filters.professions || []}
                  options={professionOptions}
                  viewMoreLabels={t(
                    "opportunity:candidates.professionFilters.viewMore"
                  )}
                  sorted={true}
                  grouped={true}
                  placeholder={t(
                    "opportunity:candidates.professionFilters.placeholder"
                  )}
                />
              )}
              {activeFilters.skills && (
                <CheckboxFilters
                  maxLength={4}
                  loading={loading}
                  title={t("opportunity:candidates.skillFilters.title")}
                  setValues={handleFiltersChange("skills")}
                  values={filters.skills || []}
                  options={skillOptions}
                  viewMoreLabels={t(
                    "opportunity:candidates.skillFilters.viewMore"
                  )}
                  sorted={true}
                  grouped={true}
                  placeholder={t(
                    "opportunity:candidates.skillFilters.placeholder"
                  )}
                />
              )}
              {activeFilters.workexperience && (
                <ExperienceFilters
                  onChange={handleFiltersChange("workexperience")}
                  loading={loading}
                  value={filters.workexperience || []}
                />
              )}
              {activeFilters.referral && (
                <ReferralFilter
                  loading={loading}
                  active={filters.referral}
                  onChange={handleFiltersChange("referral")}
                />
              )}
              {activeFilters.expectedsalary && (
                <SalaryFilter
                  onChange={handleFiltersChange("expectedsalary")}
                  value={filters.expectedsalary}
                />
              )}
              {activeFilters.languages && (
                <CheckboxFilters
                  maxLength={6}
                  loading={loading}
                  title={t("opportunity:candidates.languageFilters.title")}
                  setValues={handleFiltersChange("languages")}
                  values={filters.languages || []}
                  options={languageOptions}
                  viewMoreLabels={t(
                    "opportunity:candidates.languageFilters.viewMore"
                  )}
                  sorted={false}
                  grouped
                  placeholder={t(
                    "opportunity:candidates.languageFilters.placeholder"
                  )}
                />
              )}
              {activeFilters.education && (
                <CheckboxFilters
                  maxLength={4}
                  loading={loading}
                  title={t("opportunity:candidates.educationFilters.title")}
                  setValues={handleFiltersChange("education")}
                  values={filters.education || []}
                  options={educationOptions}
                  viewMoreLabels={t(
                    "opportunity:candidates.educationFilters.viewMore"
                  )}
                  placeholder={t(
                    "opportunity:candidates.educationFilters.placeholder"
                  )}
                />
              )}
              {activeFilters.agerange && (
                <AgeFilters
                  onChange={handleFiltersChange("agerange")}
                  loading={loading}
                  value={filters.agerange || []}
                />
              )}
              {(activeFilters.country ||
                activeFilters.province ||
                activeFilters.district) && (
                <AddressFilter
                  loading={loading}
                  setCountry={handleFiltersChange("country")}
                  setProvince={handleFiltersChange("province")}
                  setDistrict={handleFiltersChange("district")}
                  country={filters.country || []}
                  province={filters.province || []}
                  district={filters.district || []}
                />
              )}
              {activeFilters.chatbotTags &&
                chatbotTags &&
                chatbotTags.length > 0 && (
                  <ChatbotTagsFilters
                    loading={loading}
                    tags={chatbotTags}
                    value={filters.chatbotTags}
                    onChange={handleFiltersChange("chatbotTags")}
                  />
                )}
              {activeFilters.tags && tags && tags.length > 0 && (
                <CandidateTagsFilters
                  loading={loading}
                  tags={tags}
                  value={filters.tags}
                  onChange={handleFiltersChange("tags")}
                />
              )}
              {/* TODO add later <SkillsFilter /> */}
            </Sidebar>
          </Wrapper>
        )}
        <Cards>
          {list === CANDIDATE_LISTS.suggested && (
            <SortBy
              loading={loading}
              onChange={handleFiltersChange("sort")}
              value={filters.sort}
            />
          )}
          {loading ? (
            <SkeletonCandidates showGrid cardsNumber={hasFilters ? 6 : 8} />
          ) : isEmpty ? (
            <NonResults
              title={t(
                `opportunity:noResults.${
                  areFiltersApplied
                    ? "emptyResults"
                    : emptyListMessages[list] || "emptyCandidateList"
                }`
              )}
              messages={
                // TODO remove this message
                activeFilters.registered && R.isEmpty(filters.sources)
                  ? [t("opportunity:noResults.noSource")]
                  : [
                      t("opportunity:noResults.wrongList"),
                      filters.searchText !== "" &&
                        t("opportunity:noResults.orthography"),
                      (R.length(filters.strictFilters) > 0 ||
                        R.length(filters.sources) < 2) &&
                        t("opportunity:noResults.filters")
                    ].filter(Boolean)
              }
            />
          ) : (
            <ConditionalWrapper
              condition={activeFilters.page}
              falseWrapper={children => (
                <Pagination
                  style={{ flex: "1" }}
                  data={sortedFilteredCandidates}
                  pageSize={CANDIDATES_PER_PAGE}
                >
                  {children}
                </Pagination>
              )}
              trueWrapper={children =>
                mappedMetadata > CANDIDATES_PER_PAGE ? (
                  <CustomPagination
                    page={filters.page}
                    quantity={Math.ceil(mappedMetadata / CANDIDATES_PER_PAGE)}
                    onChange={handleFiltersChange("page")}
                  >
                    {children({ data: sortedFilteredCandidates })}
                  </CustomPagination>
                ) : (
                  children({ data: sortedFilteredCandidates })
                )
              }
            >
              {({ data: candidates }) => (
                <Grid>
                  {candidates.map(candidate => {
                    return (
                      <CandidateListCard
                        t={t}
                        isRecent={candidate.isRecent}
                        isSelected={Boolean(
                          R.find(
                            R.propEq("id", R.prop("id", candidate)),
                            multipleSelectedCandidates
                          )
                        )}
                        onSelect={
                          multipleSelectCandidate
                            ? selected =>
                                multipleSelectCandidate({
                                  candidate,
                                  selected
                                })
                            : undefined
                        }
                        canMove={canMove}
                        selectedCandidates={selectedCandidatesForOpportunity}
                        selectedCandidatesLoading={
                          selectedCandidatesForOpportunityUnavailable
                        }
                        restrictAdvanceToProcessByScreening={
                          restrictAdvanceToProcessByScreening
                        }
                        key={candidate.id}
                        candidate={candidate}
                        history={history}
                        list={list}
                        opportunity={opportunity}
                        routeMatch={routeMatch}
                        updateCandidateStatus={updateCandidateStatus}
                      />
                    );
                  })}
                </Grid>
              )}
            </ConditionalWrapper>
          )}
        </Cards>
      </Content>
      <Route
        path={`${routeMatch.url}/:candidateId/:tab?`}
        render={({ match, location }) => {
          const candidateId = match.params.candidateId;
          const candidateIndex = R.findIndex(
            R.propEq("id", candidateId),
            candidates
          );
          const candidate = candidateIndex >= 0 && candidates[candidateIndex];

          const onNext =
            candidate &&
            candidates[candidateIndex + 1] &&
            (() => {
              history.push(
                `${routeMatch.url}/${candidates[candidateIndex + 1].id}/info`
              );
            });

          const onPrev =
            candidate &&
            candidates[candidateIndex - 1] &&
            (() => {
              history.push(
                `${routeMatch.url}/${candidates[candidateIndex - 1].id}/info`
              );
            });

          return (
            <CandidateDetails
              onSelect={
                multipleSelectCandidate
                  ? selected =>
                      multipleSelectCandidate({
                        candidate,
                        selected
                      })
                  : undefined
              }
              isSelected={Boolean(
                R.find(R.propEq("id", candidateId), multipleSelectedCandidates)
              )}
              key={candidateId}
              listCandidate={candidate}
              isRegistered={R.propOr(true, "isRegistered", candidate)}
              interaction={R.prop("interaction", candidate)}
              candidateId={candidateId}
              closeModal={() => history.push(routeMatch.url)}
              location={location}
              canMove={canMove}
              canChat={canChat}
              selectedCandidates={selectedCandidatesForOpportunity}
              selectedCandidatesLoading={
                selectedCandidatesForOpportunityUnavailable
              }
              restrictAdvanceToProcessByScreening={
                restrictAdvanceToProcessByScreening
              }
              loading={loading}
              opportunityId={opportunity.id}
              updateCandidateStatus={updateCandidateStatus}
              list={list}
              onNext={onNext}
              onPrev={onPrev}
            />
          );
        }}
      />

      <ActionsSnackbar
        candidates={candidates}
        actions={snackbarActions}
        actionsTitle={`${R.length(multipleSelectedCandidates)}`}
        actionsDescription={t(
          `opportunity:candidates.multipleSelection.${
            plurals ? "selectedCandidates" : "selectedCandidate"
          }`
        )}
        onClose={resetMultipleSelectCandidates}
        visible={!loading && R.not(R.isEmpty(multipleSelectedCandidates))}
        onSelectAll={handleSelectAllChange}
        selectAllStatus={selectAllStatus}
      />
    </>
  );
};

const enhancer = connect(
  null,
  { markCandidatesAsRead }
);

export default enhancer(CandidatesSection);
